// Battery gauge — a battery body with a fill proportional to charge,
// colour-coded by level, plus the percentage. `px` is the body height;
// everything scales off it. Shared by the heatmap cards and the phones
// table; `charging` / `title` are opt-in extras only the table passes.

interface BatteryGaugeProps {
  /** 0–100; null (no telemetry) renders an empty body and a dash. */
  pct: number | null
  px: number
  /** Drop the "%" text — used when the gauge is the card's main glyph. */
  withText?: boolean
  /** Overlay a charging bolt on the body. */
  charging?: boolean
  /** Native tooltip text for the root element. */
  title?: string
}

function fillColor(pct: number): string {
  if (pct <= 12) return '#ef4444' // red — critical
  if (pct <= 25) return '#f59e0b' // amber — low
  return '#22c55e' // green — ok
}

export function BatteryGauge({
  pct,
  px,
  withText = true,
  charging = false,
  title,
}: BatteryGaugeProps) {
  const value = pct ?? 0
  const bodyW = px * 1.95
  const label = pct == null ? 'Заряд неизвестен' : `Заряд ${pct}%`
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: px * 0.35 }}
      aria-label={charging ? `${label}, зарядка` : label}
      title={title}
    >
      <div
        style={{
          position: 'relative',
          width: bodyW,
          height: px,
          borderRadius: px * 0.2,
          border: '1.6px solid #64748b',
          padding: 1.6,
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            height: '100%',
            width: pct == null ? 0 : `${Math.max(7, value)}%`,
            background: fillColor(value),
            borderRadius: px * 0.1,
          }}
        />
        {/* battery nub */}
        <div
          style={{
            position: 'absolute',
            right: -3.4,
            top: px * 0.3,
            width: 2.6,
            height: px * 0.4,
            background: '#64748b',
            borderRadius: 1,
          }}
        />
        {/* Charging bolt centred over the body. SVG (not the ⚡ emoji) so the
            white fill + dark stroke stay legible on any fill colour — emoji
            presentation ignores `color`. */}
        {charging && (
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            style={{
              position: 'absolute',
              inset: 0,
              margin: 'auto',
              width: px * 0.85,
              height: px * 0.85,
            }}
          >
            <path
              d="M13 3 L6 13 h4 l-1 8 8-11 h-5 z"
              fill="#fff"
              stroke="#0f172a"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
      {withText && (
        <span
          style={{
            fontSize: px * 0.92,
            color: '#94a3b8',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {pct == null ? '—' : `${pct}%`}
        </span>
      )}
    </div>
  )
}
